import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { useApp } from "@/lib/store";

export type RetrievalReadinessData = {
  glossary: { terms: number; lexical_entries: number; available: boolean; reason: string };
  documents: { sources: number; documents: number; chunks: number; embeddings: boolean; available: boolean; reason: string };
  graph: { edges: number; available: boolean; reason: string };
  vector: { available: boolean; reason: string };
  sample_queries: string[];
  filter_options: Record<string, string[]>;
  sources: Array<{ id: string; name: string; documents: number; chunks: number;
    embedded: boolean; last_sync: string | null; metadata_profile: string | null;
    available: boolean; reason: string; selected: boolean }>;
};

export function useRetrievalReadiness(profileId: string, filters?: Record<string, string>) {
  const workspace = useApp((state) => state.workspaceId);
  const filterKey = JSON.stringify(filters || {});
  return useQuery<RetrievalReadinessData>({
    queryKey: ["retrieval-readiness", workspace, profileId, filterKey],
    queryFn: () => {
      const query = new URLSearchParams({ workspace: workspace! });
      if (filters) Object.entries(filters).forEach(([key, value]) => query.set(`filter_${key}`, value));
      return rawFetch(`/retrieval/profiles/${profileId}/readiness/?${query.toString()}`);
    },
    enabled: Boolean(profileId && workspace),
  });
}

export function RetrievalReadiness({ profileId, onSample }: {
  profileId: string; onSample: (query: string) => void;
}) {
  const readiness = useRetrievalReadiness(profileId);
  if (!profileId) return <div className="card mb-4 text-sm text-muted">Choose a profile to see what it can search.</div>;
  if (readiness.isLoading) return <div className="card mb-4 text-sm text-muted">Checking searchable content…</div>;
  if (readiness.isError || !readiness.data) return <div className="card mb-4 text-sm text-bad" role="alert">Could not check this profile’s content. Refresh and try again.</div>;
  const data = readiness.data;
  const cards = [
    { title: "Glossary", ready: data.glossary.available,
      detail: `${data.glossary.terms} approved terms${data.glossary.lexical_entries ? ` · ${data.glossary.lexical_entries} ontology labels` : ""}`,
      reason: data.glossary.reason, to: "/ontology/term-sets" as const },
    { title: "Documents", ready: data.documents.available,
      detail: `${data.documents.sources} sources · ${data.documents.chunks} indexed passages · embeddings ${data.documents.embeddings ? "yes" : "no"}`,
      reason: data.documents.reason || data.vector.reason, to: "/catalog" as const },
    { title: "Graph", ready: data.graph.available,
      detail: `${data.graph.edges} permitted relationships`,
      reason: data.graph.reason, to: "/ontology" as const },
  ];
  return <section className="card mb-4" aria-label="What this profile can search right now">
    <h2 className="text-sm font-semibold">What can this profile search right now?</h2>
    <div className="mt-3 grid gap-2 md:grid-cols-3">{cards.map((card) => <div key={card.title} className="border border-border p-3 text-sm">
      <div className="flex items-center justify-between gap-2"><strong>{card.title}</strong>
        <span className={card.ready ? "pill-good" : "pill-warn"}>{card.ready ? "Available" : "Unavailable"}</span></div>
      <p className="mt-1 text-muted">{card.detail}</p>
      {!card.ready && <p className="mt-2 text-xs text-muted">{card.reason}. <Link to={card.to} className="text-accent underline">Set this up</Link></p>}
    </div>)}</div>
    {data.sample_queries.length > 0 && <div className="mt-3 text-sm"><span className="text-muted">Try a question: </span>
      <div className="mt-1 flex flex-wrap gap-2">{data.sample_queries.map((sample) => <button key={sample} type="button"
        className="btn-secondary text-xs" onClick={() => onSample(sample)}>{sample}</button>)}</div>
    </div>}
  </section>;
}
